import { Box, Typography, styled } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridPinnedColumnFields,
  GridRenderCellParams,
  GridRowHeightParams,
} from "@mui/x-data-grid-pro";
import React, { createContext, memo, useContext, useMemo } from "react";
import { FLIGHT_STATUS_CHIP_HEIGHT, FlightStatusChip } from "./FlightStatusChip";
import { IDayColumn, IRouteRow } from "./pairingStatusSelectors";
import { IFlightPairingStatusRow } from "./pairingStatusTypes";

/**
 * Calendar view: one row per flight number + routing, one column per day, a status chip per flight.
 * Rendered with DataGridPro so both rows and columns are virtualised; a two-month range with a thousand
 * routes stays responsive.
 */

export interface IPairingStatusGridInteraction {
  selectedFlightId: string | null;
  highlightedFlightId: string | null;
  onSelect: (row: IFlightPairingStatusRow) => void;
}

/** Selection/hover state is provided through context so the (expensive) column definitions stay stable. */
export const PairingStatusGridContext = createContext<IPairingStatusGridInteraction>({
  selectedFlightId: null,
  highlightedFlightId: null,
  onSelect: () => undefined,
});

const DayCell = memo(({ rows }: { rows: IFlightPairingStatusRow[] }): JSX.Element | null => {
  const { selectedFlightId, highlightedFlightId, onSelect } = useContext(PairingStatusGridContext);
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
      {rows.map((row) => (
        <FlightStatusChip
          key={row.flightId}
          row={row}
          selected={row.flightId === selectedFlightId}
          highlighted={row.flightId === highlightedFlightId}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
});
DayCell.displayName = "DayCell";

const GridContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  minHeight: 0,
  "& .MuiDataGrid-root": { border: "none", fontSize: 12 },
  "& .MuiDataGrid-cell": { padding: "2px 4px", display: "flex", alignItems: "flex-start" },
  "& .MuiDataGrid-cell.fps_day_cell": { padding: "2px 3px" },
  "& .MuiDataGrid-columnHeader": { padding: "0px 4px" },
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: 11 },
  "& .fps_weekend": { backgroundColor: theme.custom.timeline?.contextListBackground },
  "& .fps_today": {
    boxShadow: `inset 0 0 0 1px ${theme.palette.secondary.main}`,
  },
  "& .MuiDataGrid-columnHeader.fps_today .MuiDataGrid-columnHeaderTitle": { color: theme.palette.secondary.main },
}));

const NoRows = ({ message }: { message: string }): JSX.Element => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
    <Typography variant="body2" color="textSecondary">
      {message}
    </Typography>
  </Box>
);

interface IPairingStatusCalendarGridProps {
  routes: IRouteRow[];
  dayColumns: IDayColumn[];
  loading: boolean;
  emptyMessage: string;
}

const ROW_PADDING = 6;

// Stable prop values: DataGridPro re-measures every row when these change identity.
const PINNED_COLUMNS: GridPinnedColumnFields = { left: ["flightNumber", "origin", "destination"] };
const INITIAL_STATE = { sorting: { sortModel: [{ field: "flightNumber", sort: "asc" as const }] } };
const getRowHeight = (params: GridRowHeightParams): number =>
  ROW_PADDING + Math.max(1, (params.model as IRouteRow).maxPerDay) * FLIGHT_STATUS_CHIP_HEIGHT;
const numericSort = (a: number, b: number): number => a - b;

export const PairingStatusCalendarGrid = (props: IPairingStatusCalendarGridProps): JSX.Element => {
  const { routes, dayColumns, loading, emptyMessage } = props;

  const columns = useMemo<GridColDef<IRouteRow>[]>(() => {
    const fixed: GridColDef<IRouteRow>[] = [
      // Plain string rendering: a "number" column would print 1,234 with a thousands separator.
      { field: "flightNumber", headerName: "Flight", width: 70, sortComparator: numericSort },
      { field: "origin", headerName: "Orig.", width: 58 },
      { field: "destination", headerName: "Dest.", width: 58 },
    ];
    const days: GridColDef<IRouteRow>[] = dayColumns.map((day) => ({
      field: day.key,
      headerName: day.label,
      description: day.description,
      width: 42,
      minWidth: 42,
      sortable: false,
      filterable: false,
      resizable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      cellClassName: `fps_day_cell${day.isWeekend ? " fps_weekend" : ""}${day.isToday ? " fps_today" : ""}`,
      headerClassName: `${day.isWeekend ? "fps_weekend" : ""}${day.isToday ? " fps_today" : ""}`,
      valueGetter: (_value, row) => row.days[day.key]?.length ?? 0,
      renderCell: (params: GridRenderCellParams<IRouteRow>) => <DayCell rows={params.row.days[day.key]} />,
    }));
    return [...fixed, ...days];
  }, [dayColumns]);

  const slots = useMemo(() => ({ noRowsOverlay: () => <NoRows message={emptyMessage} /> }), [emptyMessage]);

  return (
    <GridContainer>
      <DataGridPro
        rows={routes}
        columns={columns}
        loading={loading}
        columnHeaderHeight={30}
        getRowHeight={getRowHeight}
        pinnedColumns={PINNED_COLUMNS}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        hideFooter
        initialState={INITIAL_STATE}
        slots={slots}
      />
    </GridContainer>
  );
};
